import * as React from 'react';
import { ActionGroup, Alert, Button, Flex, FlexItem, Form, FormAlert, FormGroup, PageSection, TextInput, Title } from '@patternfly/react-core';
import { UserContext } from '@app/Context/UserContext';
import UserAuthentication from '@app/Security/UserAuthentication';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import ProducerInterface from '@app/Interface/ProducerInterface';
import ProducerAddressInterface, { AddressType } from '@app/Interface/ProducerAddressInterface';
import FpuAddressFunctions from './FpuAddressFunctions';
import ItemLoader from '@app/Components/ItemLoader';
import ProducerAddressHelper from '@app/Service/ProducerAddressHelper';
import FieldsInterface from '@app/Interface/FieldsInterface';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { ToastNotificationType } from '@app/Interface/ToastNotificationInterface';
import { CreateToastNotification, ToastNotificationContext } from '@app/Context/ToastNotificationContext';
import ToastNotifications from '@app/Components/ToastNotification/ToastNotifications';
import { PageRouteContext } from '@app/Context/PageRouteContext';
import PageRouteHelper from "@app/Service/PageRouteHelper";
import MyFpuComponentViewInterface from '@app/Interface/MyFpuComponentViewInterface';
import MyFpuComponentViewModel from '@app/Model/MyFpuComponentViewModel';
import { PermissionDenied } from '@app/PermissionDenied/PermissionDenied';

interface FpuAddressesProps {
    producerId: number;
    producer: ProducerInterface;
    buttonHandler: (component: MyFpuComponentViewInterface) => void;
}

const FpuAddresses: React.FC<FpuAddressesProps> = ({ producerId, producer, buttonHandler }) => {

    // NOTE: ToastNotificationContext provider was added to `src\app\index.tsx`
    const { toastDispatch } = React.useContext(ToastNotificationContext);

    const { pageRouteState } = React.useContext(PageRouteContext);

    const { userState } = React.useContext(UserContext);

    // use to generate link to view fpu list page.
    const viewFpuPageRoute = PageRouteHelper.getPageRouteByTemplate(pageRouteState.pageRoutes, TemplateDefinitions.TEMPLATE_VIEW_FPU_LIST_PAGE);

    // NOTE: if user permission was changed, they would need to log out/log in to see changes.
    const canViewPage = UserAuthentication.canUserViewDashboardItem(userState.user.viewPermissions, userState.user.currentFpuId, TemplateDefinitions.TEMPLATE_VIEW_FPU_LIST_PAGE);

    const [mailingAddress, setMailingAddress] = React.useState<ProducerAddressInterface>(FpuAddressFunctions.getDefaultAddressesByType(AddressType.Mailing));

    const [farmAddress, setFarmAddress] = React.useState<ProducerAddressInterface>(FpuAddressFunctions.getDefaultAddressesByType(AddressType.Farm));

    const [isLoading, setIsLoading] = React.useState(true);

    const initialFieldValues: FieldsInterface = FpuAddressFunctions.getAddressFields(farmAddress, mailingAddress);

    const [formFields, setFormFields] = React.useState<FieldsInterface>(initialFieldValues);

    // used when the rest button is clicked
    const [originalFields, setOriginalFields] = React.useState<FieldsInterface>({ ...initialFieldValues });

    const [isFormProcessing, setIsFormProcessing] = React.useState(false);

    // get addresses for the fpu 
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function getAddresses() {

            const fpuAddresses = await ProducerAddressHelper.getAddresses(controller, Number(producerId), viewFpuPageRoute.id);

            if (mounted) {
                if (fpuAddresses.length) {

                    const tmpMailingAddress = FpuAddressFunctions.findAddressByType(fpuAddresses, AddressType.Mailing);

                    const tmpFarmAddress = FpuAddressFunctions.findAddressByType(fpuAddresses, AddressType.Farm);

                    const tmpFormFields = FpuAddressFunctions.getAddressFields(tmpFarmAddress, tmpMailingAddress);

                    setMailingAddress(tmpMailingAddress);

                    setFarmAddress(tmpFarmAddress);

                    // perform deep copy
                    setOriginalFields(JSON.parse(JSON.stringify(tmpFormFields)));

                    // shallow copy. only get for primitive types, if it an object, then that it needs deep copy
                    setFormFields({ ...tmpFormFields });

                }

                setIsLoading(false);
            }

        }

        if (producerId && viewFpuPageRoute.id) {
            getAddresses();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [producerId, viewFpuPageRoute.id]);

    const defaultFormValidated = "default";

    // for values: see https://www.patternfly.org/v4/components/form#formgroup
    const [requiredValidated, setRequiredValidated] = React.useState(defaultFormValidated);

    const handleChange = (newValue: string, e: React.FormEvent<HTMLInputElement>) => {

        const fieldId = e.currentTarget.id;

        const updatedFields = { ...formFields };

        updatedFields[fieldId].value = newValue;

        setFormFields(updatedFields);

    }

    /**
     * Resets to previous values by loading current user information
     * @param e 
     */
    const handleReset = (e: React.FormEvent<HTMLButtonElement>) => {

        e.preventDefault();

        setFormFields(originalFields);

    }

    const handleSubmit = async (e: React.SyntheticEvent) => {

        e.preventDefault();

        setIsFormProcessing(true);

        setRequiredValidated("default");

        const [isValid, updatedFormFields] = FpuAddressFunctions.formValidation(formFields);

        if (isValid) {

            const updateFarmAddress = FpuAddressFunctions.didAddressChange(formFields, farmAddress, AddressType.Farm);

            const updateMailingAddress = FpuAddressFunctions.didAddressChange(formFields, mailingAddress, AddressType.Mailing);

            if (!updateFarmAddress && !updateMailingAddress) {

                const notificationType: ToastNotificationType = ToastNotificationType.Warning;

                const toastTitle = "No changes";

                const tastDescription = "No changes were detected, request was not sent.";

                CreateToastNotification(toastDispatch, notificationType, toastTitle, tastDescription);

            } else {

                if (updateFarmAddress) {

                    await sendAddressNotification(AddressType.Farm);

                }

                if (updateMailingAddress) {

                    await sendAddressNotification(AddressType.Mailing);

                }

            }

        } else {

            setRequiredValidated("error");

            setFormFields(updatedFormFields);

        }

        setIsFormProcessing(false);

    }

    const sendAddressNotification = async (addressType: AddressType) => {

        let notificationType: ToastNotificationType = ToastNotificationType.Default;

        let toastTitle = "";

        let tastDescription = "";

        const data = FpuAddressFunctions.getAddressData(Number(producerId), formFields, addressType);

        const responseCode = await ProducerAddressHelper.sendAddressUpdateNotification(data);

        let addressTypeName = "Farm";

        if (addressType === AddressType.Mailing) {
            addressTypeName = "Mailing";
        }

        switch (responseCode) {

            case 200:
                notificationType = ToastNotificationType.Success;
                toastTitle = `${addressTypeName} Address Request Sent`;
                tastDescription = `A notification was sent to Darigold to update your FPU ${addressTypeName} addresses.`;
                break;

            case 400:
                notificationType = ToastNotificationType.Danger;
                toastTitle = `Invalid ${addressTypeName} address input`;
                tastDescription = "Please try again or contact support if the issue persists.";
                break;

            case 404:
                notificationType = ToastNotificationType.Danger;
                toastTitle = `${addressTypeName} Resource not found`;
                tastDescription = "Endpoint to send the addresses was invalid, try again or contact support.";
                break;

            case 422:
                notificationType = ToastNotificationType.Danger;
                toastTitle = `Bad ${addressTypeName} address data`;
                tastDescription = "Error related to bad address data prevented sending request, try again or contact support.";
                break;

            default:
                notificationType = ToastNotificationType.Warning;
                toastTitle = `${addressTypeName} Address - Unknown error`;
                tastDescription = "Unable to send address data, try again or contact support.";
                break;

        }

        CreateToastNotification(toastDispatch, notificationType, toastTitle, tastDescription);

    }

    React.useEffect(() => {

        return () => {
            
            // on unmount, clear all toast messages
            toastDispatch({
                type: "CLEAR"
            });

        };

    }, [toastDispatch]);

    if (canViewPage) {

        const viewFpuList = new MyFpuComponentViewModel();

        return (
            <ToastNotifications>
                <PageSection>
                    <Button
                        variant="primary"
                        onClick={() => buttonHandler(viewFpuList)}
                    >
                        Return to FPU List
                    </Button>
                    <Title headingLevel="h1" className="pf-u-mb-md">Manage FPU Addresses</Title>
                    <Title headingLevel="h2">{producer.alphaName} - {producerId}</Title>

                    {isLoading ? <ItemLoader /> :

                        <Form autoComplete="false" isWidthLimited onSubmit={handleSubmit}>

                            <div className="pf-u-mb-md">

                                <p className="pf-u-mb-md">If you recently submitted an address change, the current data may not reflect the requested updates.</p>

                                <Flex>
                                    <FlexItem>
                                        <Title headingLevel="h3" className="pf-u-mb-0">Current Farm Addresses</Title>
                                        {farmAddress.addressId === 0
                                            ? <div>No information can be found</div>
                                            : <div>
                                                {farmAddress.street1}<br />
                                                {farmAddress.street2.length ? <>{farmAddress.street2}<br /></> : null}
                                                {farmAddress.city}, {farmAddress.state} {farmAddress.postal}
                                            </div>}
                                    </FlexItem>
                                    <FlexItem>
                                        <Title headingLevel="h3" className="pf-u-mb-0">Current Mailing Addresses</Title>
                                        {mailingAddress.addressId === 0
                                            ? <div>No information can be found</div>
                                            : <div>
                                                {mailingAddress.street1}<br />
                                                {mailingAddress.street2.length ? <>{mailingAddress.street2}<br /></> : null}
                                                {mailingAddress.city}, {mailingAddress.state} {mailingAddress.postal}
                                            </div>}
                                    </FlexItem>
                                </Flex>

                            </div>

                            <Title headingLevel="h2" className="pf-u-mb-0">Request Updates</Title>

                            {requiredValidated === "error" && (
                                <FormAlert>
                                    <Alert
                                        variant="danger"
                                        title="Fill out all required fields before continuing"
                                        aria-live="polite"
                                        role="alert"
                                        isInline
                                    ></Alert>
                                </FormAlert>
                            )}

                            <Title headingLevel="h3" className="pf-u-mb-0">Farm Address</Title>

                            {farmAddress.addressId === 0 ? <div>Initial address information is missing! Contact Darigold</div> :
                                <>
                                    <FormGroup
                                        label={formFields["farmStreet1"].label}
                                        isRequired={formFields["farmStreet1"].required}
                                        fieldId={formFields["farmStreet1"].formFieldId}
                                        helperTextInvalid={formFields["farmStreet1"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["farmStreet1"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["farmStreet1"].required}
                                            type="text"
                                            id={formFields["farmStreet1"].fieldId}
                                            name={formFields["farmStreet1"].fieldId}
                                            value={typeof formFields["farmStreet1"].value !== "boolean" ? formFields["farmStreet1"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>
                                    <FormGroup
                                        label={formFields["farmStreet2"].label}
                                        isRequired={formFields["farmStreet2"].required}
                                        fieldId={formFields["farmStreet2"].formFieldId}
                                        helperTextInvalid={formFields["farmStreet2"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["farmStreet2"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["farmStreet2"].required}
                                            type="text"
                                            id={formFields["farmStreet2"].fieldId}
                                            name={formFields["farmStreet2"].fieldId}
                                            value={typeof formFields["farmStreet2"].value !== "boolean" ? formFields["farmStreet2"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>
                                    <FormGroup
                                        label={formFields["farmCity"].label}
                                        isRequired={formFields["farmCity"].required}
                                        fieldId={formFields["farmCity"].formFieldId}
                                        helperTextInvalid={formFields["farmCity"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["farmCity"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["farmCity"].required}
                                            type="text"
                                            id={formFields["farmCity"].fieldId}
                                            name={formFields["farmCity"].fieldId}
                                            value={typeof formFields["farmCity"].value !== "boolean" ? formFields["farmCity"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>
                                    <FormGroup
                                        label={formFields["farmState"].label}
                                        isRequired={formFields["farmState"].required}
                                        fieldId={formFields["farmState"].formFieldId}
                                        helperTextInvalid={formFields["farmState"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["farmState"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["farmState"].required}
                                            type="text"
                                            id={formFields["farmState"].fieldId}
                                            name={formFields["farmState"].fieldId}
                                            value={typeof formFields["farmState"].value !== "boolean" ? formFields["farmState"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>

                                    <FormGroup
                                        label={formFields["farmPostal"].label}
                                        isRequired={formFields["farmPostal"].required}
                                        fieldId={formFields["farmPostal"].formFieldId}
                                        helperTextInvalid={formFields["farmPostal"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["farmPostal"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["farmPostal"].required}
                                            type="text"
                                            id={formFields["farmPostal"].fieldId}
                                            name={formFields["farmPostal"].fieldId}
                                            value={typeof formFields["farmPostal"].value !== "boolean" ? formFields["farmPostal"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>
                                </>
                            }

                            <Title headingLevel="h3" className="pf-u-mb-0">Mailing Address</Title>

                            {mailingAddress.addressId === 0 ? <div>Initial address information is missing! Contact Darigold</div> :
                                <>
                                    <FormGroup
                                        label={formFields["mailingStreet1"].label}
                                        isRequired={formFields["mailingStreet1"].required}
                                        fieldId={formFields["mailingStreet1"].formFieldId}
                                        helperTextInvalid={formFields["mailingStreet1"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["mailingStreet1"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["mailingStreet1"].required}
                                            type="text"
                                            id={formFields["mailingStreet1"].fieldId}
                                            name={formFields["mailingStreet1"].fieldId}
                                            value={typeof formFields["mailingStreet1"].value !== "boolean" ? formFields["mailingStreet1"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>
                                    <FormGroup
                                        label={formFields["mailingStreet2"].label}
                                        isRequired={formFields["mailingStreet2"].required}
                                        fieldId={formFields["mailingStreet2"].formFieldId}
                                        helperTextInvalid={formFields["mailingStreet2"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["mailingStreet2"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["mailingStreet2"].required}
                                            type="text"
                                            id={formFields["mailingStreet2"].fieldId}
                                            name={formFields["mailingStreet2"].fieldId}
                                            value={typeof formFields["mailingStreet2"].value !== "boolean" ? formFields["mailingStreet2"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>
                                    <FormGroup
                                        label={formFields["mailingCity"].label}
                                        isRequired={formFields["mailingCity"].required}
                                        fieldId={formFields["mailingCity"].formFieldId}
                                        helperTextInvalid={formFields["mailingCity"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["mailingCity"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["mailingCity"].required}
                                            type="text"
                                            id={formFields["mailingCity"].fieldId}
                                            name={formFields["mailingCity"].fieldId}
                                            value={typeof formFields["mailingCity"].value !== "boolean" ? formFields["mailingCity"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>
                                    <FormGroup
                                        label={formFields["mailingState"].label}
                                        isRequired={formFields["mailingState"].required}
                                        fieldId={formFields["mailingState"].formFieldId}
                                        helperTextInvalid={formFields["mailingState"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["mailingState"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["mailingState"].required}
                                            type="text"
                                            id={formFields["mailingState"].fieldId}
                                            name={formFields["mailingState"].fieldId}
                                            value={typeof formFields["mailingState"].value !== "boolean" ? formFields["mailingState"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>

                                    <FormGroup
                                        label={formFields["mailingPostal"].label}
                                        isRequired={formFields["mailingPostal"].required}
                                        fieldId={formFields["mailingPostal"].formFieldId}
                                        helperTextInvalid={formFields["mailingPostal"].helperTextInvalid}
                                        helperTextInvalidIcon={<ExclamationCircleIcon />}
                                        validated={formFields["mailingPostal"].validated}
                                    >
                                        <TextInput
                                            isRequired={formFields["mailingPostal"].required}
                                            type="text"
                                            id={formFields["mailingPostal"].fieldId}
                                            name={formFields["mailingPostal"].fieldId}
                                            value={typeof formFields["mailingPostal"].value !== "boolean" ? formFields["mailingPostal"].value : ""}
                                            onChange={handleChange}
                                        />
                                    </FormGroup>
                                </>
                            }

                            <ActionGroup>
                                <Button
                                    variant="primary"
                                    onClick={handleSubmit}
                                    spinnerAriaValueText={isFormProcessing ? "Sending" : undefined}
                                    isLoading={isFormProcessing}
                                >{isFormProcessing ? "Sending" : "Request Update"}</Button>
                                <Button variant="link" onClick={handleReset}>Reset</Button>
                            </ActionGroup>

                        </Form>

                    }
                </PageSection>
            </ToastNotifications>
        );

    } else {

        return (
            <PermissionDenied />
        );

    }

};

export default FpuAddresses;
